import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Plus, ShieldOff } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useAppDispatch } from '@/Redux/store';
import { useBranches } from '@/Pages/Operations/shared';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import PanelResult from './PanelResult';
import SecretRevealDialog from './SecretRevealDialog';
import {
  ALL_API_SCOPES,
  API_CLIENTS_PERMISSIONS,
  API_CLIENTS_ROUTES,
  invalidateApiClient,
  scopeI18nKey,
  useApiClientQuery,
  useApiClientsQuery,
  type ApiClient,
  type ApiClientWithSecret,
  type ApiScope,
  type CreateApiClientBody,
  type UpdateApiClientBody,
} from '../apiClientsApi';
import { apiMessage, formatDateTime, toneForApiClientStatus } from '../lib';

/**
 * Screen 4 — ADR-0003. This venue's own machine credentials: the identities a
 * script, a POS integration or a partner's server holds to call `/public/v1`.
 *
 * Lives as a tab of Integrations rather than a screen of its own — the same
 * person who looks at Providers and Webhooks when something is broken is the
 * one who mints and kills these, and the rail already earns its row on that
 * reasoning (see `nav.ts`).
 */
export default function ApiClientsPanel() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { can } = usePermission();
  const canCreate = can(API_CLIENTS_PERMISSIONS.create);

  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<ApiClientWithSecret | null>(null);

  const { data, isFetching, isError, error, refetch } = useApiClientsQuery();
  const branches = useBranches();

  const onCreated = (issued: ApiClientWithSecret) => {
    dispatch(invalidateApiClient());
    setCreating(false);
    setIssuedSecret(issued);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="max-w-2xl text-sm text-ink-muted">{t('integrations.apiClients.subtitle')}</p>
        {canCreate && (
          <Button onClick={() => setCreating(true)}>
            <Icon as={Plus} />
            {t('integrations.apiClients.newClient')}
          </Button>
        )}
      </div>

      <PanelResult
        loading={isFetching}
        error={isError ? apiMessage(error, t('integrations.states.loadFailed')) : null}
        count={data?.length ?? 0}
        onRetry={refetch}
        emptyTitle={t('integrations.apiClients.emptyTitle')}
        emptyHint={t('integrations.apiClients.emptyHint')}
        emptyAction={
          canCreate && (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Icon as={Plus} />
              {t('integrations.apiClients.newClient')}
            </Button>
          )
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('integrations.apiClients.name')}</TH>
                <TH>{t('integrations.apiClients.scopes')}</TH>
                <TH>{t('integrations.common.branch')}</TH>
                <TH>{t('integrations.webhooks.status')}</TH>
                <TH>{t('integrations.apiClients.lastUsed')}</TH>
              </TR>
            </THead>
            <tbody>
              {(data ?? []).map((client) => (
                <TR key={client.id} className="cursor-pointer" onClick={() => setOpenId(client.id)}>
                  <TD className="font-medium">
                    {client.name}
                    <span dir="ltr" className="block font-mono text-xs text-ink-muted">
                      {client.clientId}
                    </span>
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">{client.scopes.length}</TD>
                  <TD className="text-sm text-ink-muted">
                    {branchLabel(client, branches, t('integrations.providers.wholeVenue'))}
                  </TD>
                  <TD>
                    <Badge tone={toneForApiClientStatus(client.status)}>
                      {t(`integrations.apiClientStatus.${client.status}`)}
                    </Badge>
                  </TD>
                  <TD className="text-sm tabular-nums text-ink-muted">
                    {formatDateTime(client.lastUsedAt, i18n.language)}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      {creating && <CreateClientDialog onClose={() => setCreating(false)} onCreated={onCreated} />}

      <SecretRevealDialog
        open={issuedSecret !== null}
        secret={issuedSecret?.clientSecret ?? null}
        title={t('integrations.apiClients.secretTitle')}
        intro={t('integrations.apiClients.secretIntro')}
        onClose={() => setIssuedSecret(null)}
      />

      {openId && <ApiClientDetail id={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

/**
 * The BRANCH NAME, not `branchId` — a raw cuid on a row a person is scanning
 * for "which branch is this fenced to" reads as a bug even when it isn't
 * one. Falls back to the id itself only if the branch list has not loaded
 * (or the branch is gone), so this never renders blank.
 */
function branchLabel(
  client: Pick<ApiClient, 'branchId'>,
  branches: { id: string; name: string }[],
  wholeVenueLabel: string,
): string {
  if (!client.branchId) return wholeVenueLabel;
  return branches.find((b) => b.id === client.branchId)?.name ?? client.branchId;
}

function ScopeChecklist({
  value,
  onChange,
}: {
  value: ApiScope[];
  onChange: (next: ApiScope[]) => void;
}) {
  const { t } = useTranslation();
  const toggle = (scope: ApiScope) =>
    onChange(value.includes(scope) ? value.filter((s) => s !== scope) : [...value, scope]);

  return (
    <div className="max-h-52 space-y-1.5 overflow-y-auto rounded-lg border p-2">
      {ALL_API_SCOPES.map((scope) => (
        <label key={scope} className="flex items-start gap-2 rounded p-1 text-sm hover:bg-hover">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={value.includes(scope)}
            onChange={() => toggle(scope)}
          />
          <span>
            <span dir="ltr" className="block font-mono text-xs text-ink-muted">
              {scope}
            </span>
            <span className="block">
              {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

// -------------------------------------------------------------- create

function CreateClientDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (issued: ApiClientWithSecret) => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [branchId, setBranchId] = useState('');
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState('');
  const [quotaPerDay, setQuotaPerDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || scopes.length === 0) {
      setError(t('integrations.apiClients.createValidation'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: CreateApiClientBody = {
        name: name.trim(),
        scopes,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(branchId ? { branchId } : {}),
        ...(rateLimitPerMinute ? { rateLimitPerMinute: Number(rateLimitPerMinute) } : {}),
        ...(quotaPerDay ? { quotaPerDay: Number(quotaPerDay) } : {}),
      };
      const { data } = await api.post<ApiClientWithSecret>(API_CLIENTS_ROUTES.list, body);
      toast.success(t('integrations.apiClients.created'));
      onCreated(data);
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('integrations.apiClients.newClient')}
      className="max-w-lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void submit()}>
            {t('integrations.apiClients.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('integrations.apiClients.name')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
        </Field>
        <Field label={t('integrations.webhooks.description')}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
        </Field>
        {branches.length > 0 && (
          <Field
            label={t('integrations.common.branch')}
            hint={t('integrations.apiClients.branchHint')}
          >
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">{t('integrations.providers.wholeVenue')}</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('integrations.apiClients.scopes')} required>
          <ScopeChecklist value={scopes} onChange={setScopes} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label={t('integrations.apiClients.rateLimitPerMinute')}
            hint={t('integrations.apiClients.rateLimitPerMinuteHint')}
          >
            <Input
              type="number"
              min={1}
              max={6000}
              value={rateLimitPerMinute}
              onChange={(e) => setRateLimitPerMinute(e.target.value)}
              placeholder="120"
            />
          </Field>
          <Field
            label={t('integrations.apiClients.quotaPerDay')}
            hint={t('integrations.apiClients.quotaPerDayHint')}
          >
            <Input
              type="number"
              min={1}
              value={quotaPerDay}
              onChange={(e) => setQuotaPerDay(e.target.value)}
              placeholder={t('integrations.apiClients.quotaNoCeiling')}
            />
          </Field>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// -------------------------------------------------------------- detail

function ApiClientDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { confirm } = useConfirm();
  const { can } = usePermission();
  const canUpdate = can(API_CLIENTS_PERMISSIONS.update);
  const canRevoke = can(API_CLIENTS_PERMISSIONS.revoke);

  const { data, isFetching, isError, error, refetch } = useApiClientQuery(id);
  const reload = () => {
    refetch();
    dispatch(invalidateApiClient(id));
  };

  const [editing, setEditing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [issuedSecret, setIssuedSecret] = useState<ApiClientWithSecret | null>(null);

  const toggleSuspended = async () => {
    if (!data) return;
    try {
      const next: UpdateApiClientBody = {
        status: data.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE',
      };
      await api.patch(API_CLIENTS_ROUTES.one(id), next);
      toast.success(
        next.status === 'SUSPENDED'
          ? t('integrations.apiClients.suspended')
          : t('integrations.apiClients.resumed'),
      );
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  const rotate = async () => {
    setRotating(true);
    try {
      const { data: issued } = await api.post<ApiClientWithSecret>(API_CLIENTS_ROUTES.rotateSecret(id));
      setIssuedSecret(issued);
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setRotating(false);
    }
  };

  const revoke = async () => {
    if (!data) return;
    const ok = await confirm({
      title: t('integrations.apiClients.revokeTitle', { name: data.name }),
      body: t('integrations.apiClients.revokeBody'),
      confirmLabel: t('integrations.apiClients.revoke'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(API_CLIENTS_ROUTES.one(id));
      toast.success(t('integrations.apiClients.revoked'));
      reload();
    } catch (requestError) {
      toast.error(apiMessage(requestError, t('integrations.errors.actionFailed')));
    }
  };

  const revoked = data?.status === 'REVOKED';

  return (
    <Dialog
      open
      onClose={onClose}
      title={data?.name ?? t('integrations.apiClients.detailTitle')}
      description={data?.clientId}
      className="max-w-2xl"
      footer={
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {/*
                Revoke is its own control on its own permission key, deliberately
                never folded in with rotate/suspend/edit below (`api_clients.update`).
                ADR-0003: the split exists so an on-call responder can be handed
                the power to kill a leaked secret without also being handed the
                power to mint one — collapsing these two buttons into one gate
                would quietly regrant the second half of that split.
            */}
            {data && !revoked && canRevoke && (
              <Button variant="danger" onClick={() => void revoke()}>
                <Icon as={ShieldOff} />
                {t('integrations.apiClients.revoke')}
              </Button>
            )}
          </div>
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      {isFetching && !data && <LoadingState />}
      {isError && !data && (
        <ErrorState message={apiMessage(error, t('integrations.states.loadFailed'))} onRetry={reload} />
      )}
      {data && (
        <div className="max-h-[70vh] space-y-6 overflow-y-auto pe-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={toneForApiClientStatus(data.status)}>
              {t(`integrations.apiClientStatus.${data.status}`)}
            </Badge>
            <span dir="ltr" className="font-mono text-xs text-ink-muted">
              {data.secretPrefix}…
            </span>
          </div>

          {revoked && (
            <div className="rounded-lg border p-3 text-sm text-ink-muted">
              {t('integrations.apiClients.revokedNotice', {
                date: formatDateTime(data.revokedAt, i18n.language),
              })}
            </div>
          )}

          <section className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">{t('integrations.detail.sectionConfigure')}</h3>
              {canUpdate && !revoked && !editing && (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  {t('common.edit')}
                </Button>
              )}
            </div>

            {!editing ? (
              <ClientOverview client={data} />
            ) : (
              <ClientEditForm
                client={data}
                onCancel={() => setEditing(false)}
                onSaved={() => {
                  setEditing(false);
                  reload();
                }}
              />
            )}

            {/*
                Suspension is immediate — `ApiClientGate.resolve()` re-reads
                `ApiClient.status` on every single call to /public/v1, so this
                is not "at next token expiry" the way a webhook's disable-window
                sometimes reads. Said plainly, next to the control that does it.
            */}
            {canUpdate && !revoked && !editing && (
              <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm dark:border-amber-900 dark:bg-amber-950/30">
                <p>
                  {data.status === 'ACTIVE'
                    ? t('integrations.apiClients.suspendExplainer')
                    : t('integrations.apiClients.resumeExplainer')}
                </p>
                <Button size="sm" variant="outline" onClick={() => void toggleSuspended()}>
                  {data.status === 'ACTIVE'
                    ? t('integrations.apiClients.suspend')
                    : t('integrations.apiClients.resume')}
                </Button>
              </div>
            )}
          </section>

          <section className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">{t('integrations.apiClients.sectionLimits')}</h3>
              {canUpdate && !revoked && (
                <Button size="sm" variant="outline" onClick={() => void rotate()} disabled={rotating}>
                  <Icon as={KeyRound} />
                  {t('integrations.apiClients.rotateSecret')}
                </Button>
              )}
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-ink-muted">{t('integrations.apiClients.rateLimitPerMinute')}</dt>
                <dd className="tabular-nums">{data.rateLimitPerMinute}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">{t('integrations.apiClients.quotaPerDay')}</dt>
                <dd className="tabular-nums">
                  {data.quotaPerDay ?? t('integrations.apiClients.quotaNoCeiling')}
                </dd>
              </div>
              <div>
                <dt className="text-ink-muted">{t('integrations.apiClients.lastUsed')}</dt>
                <dd>{formatDateTime(data.lastUsedAt, i18n.language)}</dd>
              </div>
              <div>
                <dt className="text-ink-muted">{t('integrations.apiClients.createdAt')}</dt>
                <dd>{formatDateTime(data.createdAt, i18n.language)}</dd>
              </div>
            </dl>
            {/*
                Honest about what this screen can and cannot show: the backend
                exposes the CEILING (`rateLimitPerMinute`, `quotaPerDay`) and
                `lastUsedAt`, but no route returns this client's day-by-day
                consumption — `api_usage_counters` (ADR-0003) is written but
                never read back for a plain internal client, unlike a devportal
                sandbox credential's `GET .../sandbox/usage`. Said here rather
                than faked with a progress bar that cannot be trusted.
            */}
            <p className="text-xs text-ink-muted">{t('integrations.apiClients.noUsageReadout')}</p>
          </section>

          {/* Rotating here is always immediate — see `apiClientsApi.ts`'s header note on why no overlap control is offered. */}
          <p className="border-t pt-4 text-xs text-ink-muted">
            {t('integrations.apiClients.rotateExplainer')}
          </p>
        </div>
      )}

      <SecretRevealDialog
        open={issuedSecret !== null}
        secret={issuedSecret?.clientSecret ?? null}
        title={t('integrations.apiClients.secretTitle')}
        intro={t('integrations.apiClients.rotatedIntro')}
        onClose={() => setIssuedSecret(null)}
      />
    </Dialog>
  );
}

function ClientOverview({ client }: { client: ApiClient }) {
  const { t } = useTranslation();
  const branches = useBranches();
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      <div className="sm:col-span-2">
        <dt className="text-ink-muted">{t('integrations.apiClients.scopes')}</dt>
        <dd className="space-y-1 pt-1">
          {client.scopes.map((scope) => (
            <div key={scope} className="flex flex-wrap items-baseline gap-x-2">
              <Badge tone="neutral">
                <span dir="ltr">{scope}</span>
              </Badge>
              <span className="text-sm">
                {t(`integrations.apiClients.scopeDescription.${scopeI18nKey(scope)}`)}
              </span>
            </div>
          ))}
        </dd>
      </div>
      <div>
        <dt className="text-ink-muted">{t('integrations.common.branch')}</dt>
        <dd>
          {branches.find((b) => b.id === client.branchId)?.name ??
            t('integrations.providers.wholeVenue')}
        </dd>
      </div>
      {client.description && (
        <div>
          <dt className="text-ink-muted">{t('integrations.webhooks.description')}</dt>
          <dd>{client.description}</dd>
        </div>
      )}
    </dl>
  );
}

function ClientEditForm({
  client,
  onCancel,
  onSaved,
}: {
  client: ApiClient;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const branches = useBranches();

  const [name, setName] = useState(client.name);
  const [description, setDescription] = useState(client.description ?? '');
  const [branchId, setBranchId] = useState(client.branchId ?? '');
  const [scopes, setScopes] = useState<ApiScope[]>(client.scopes);
  const [rateLimitPerMinute, setRateLimitPerMinute] = useState(String(client.rateLimitPerMinute));
  const [quotaPerDay, setQuotaPerDay] = useState(client.quotaPerDay ? String(client.quotaPerDay) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim() || scopes.length === 0) {
      setError(t('integrations.apiClients.createValidation'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: UpdateApiClientBody = {
        name: name.trim(),
        description: description.trim(),
        scopes,
        branchId: branchId || null,
        rateLimitPerMinute: rateLimitPerMinute ? Number(rateLimitPerMinute) : undefined,
        quotaPerDay: quotaPerDay ? Number(quotaPerDay) : null,
      };
      await api.patch(API_CLIENTS_ROUTES.one(client.id), body);
      toast.success(t('integrations.detail.configureSaved'));
      onSaved();
    } catch (requestError) {
      setError(apiMessage(requestError, t('integrations.errors.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Field label={t('integrations.apiClients.name')} required>
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </Field>
      <Field label={t('integrations.webhooks.description')}>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
      </Field>
      {branches.length > 0 && (
        <Field label={t('integrations.common.branch')} hint={t('integrations.apiClients.branchHint')}>
          <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">{t('integrations.providers.wholeVenue')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      <Field label={t('integrations.apiClients.scopes')} required>
        <ScopeChecklist value={scopes} onChange={setScopes} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('integrations.apiClients.rateLimitPerMinute')}>
          <Input
            type="number"
            min={1}
            max={6000}
            value={rateLimitPerMinute}
            onChange={(e) => setRateLimitPerMinute(e.target.value)}
          />
        </Field>
        <Field label={t('integrations.apiClients.quotaPerDay')}>
          <Input
            type="number"
            min={1}
            value={quotaPerDay}
            onChange={(e) => setQuotaPerDay(e.target.value)}
            placeholder={t('integrations.apiClients.quotaNoCeiling')}
          />
        </Field>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" disabled={busy} onClick={() => void save()}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
